import { useCallback } from 'react';

import { IrisVehicleImageUrl, S3ImageUrl } from '~/api/urls';
import { useVehicleOptions } from '~/contexts/VehicleOptionsContext';
import { ECameraType, EIrisCameraType } from '~/domain/ECameraType';
// import { EIrisRegion, ERegion } from '~/domain/ERegion'
import { ICamera } from '~/domain/ICamera';
import { IImageSettings } from '~/domain/IImageSettings';
import { useProductConfigurationQuery } from '~/hooks/useProductConfigurationQuery';
import { useRequest } from '~/hooks/useRequestQuery';

interface IImageProperties {
  pov: string;
  width: string;
}

interface IVehicleFeatures {
  vehicle: string;
  paint: string;
  fabric: string;
  sa: string;
}

const getImageProperties = (
  camera: ICamera,
  settings: IImageSettings
): IImageProperties => {
  // Determine our way point - we need to increment by 1 if we are given one.
  const wayPoint = settings.cameraWaypoint ? settings.cameraWaypoint + 1 : 1;

  // Pad single digit waypoints with a 0.
  const wayPointPadded = wayPoint.toString().padStart(2, '0');

  const width = settings.width.toString();

  // other camera types are not applicable to nissan
  switch (camera.type) {
    case ECameraType.EXT_360:
      return { pov: `${EIrisCameraType.EXT_360}${wayPointPadded}`, width };
    case ECameraType.INT_360:
      return { pov: `${EIrisCameraType.INT_360}${wayPointPadded}`, width };
    case ECameraType.FEATURE_RETAILER:
    case ECameraType.FEATURE_EXT:
    case ECameraType.FEATURE_INT:
      return { pov: camera.name, width };
    case ECameraType.INT_PANO:
      return { pov: EIrisCameraType.INT_PANO, width };
  }

  // default fallback - this line should not be encountered
  return { pov: EIrisCameraType.INT_PANO, width };
};

const getVehicleFeatures = (features: string[]): IVehicleFeatures | null => {
  // let brand = '';

  // switch (region) {
  // case ERegion.TESTING:   // TODO: how should we handle testing?
  // case ERegion.USA:
  //   brand = EIrisRegion.NISNNA;
  //   break;
  // case ERegion.CAN:
  //   brand = EIrisRegion.NISCAN;
  //   break;
  // case ERegion.AMI:
  //   brand = EIrisRegion.NISAMI;
  //   break;
  // case ERegion.JPN:
  //   brand = EIrisRegion.NISJPN;
  //   break;
  // case ERegion.NSAM:
  // case ERegion.NMEX:
  // case ERegion.NAE:
  // case ERegion.ASR:
  //   brand = EIrisRegion.NISGLO;
  //   break;
  // }
  // if (features.length < 16) { return null; }

  // Pull out specific details from the EIM.
  const vehicle = features[6];
  const paint = features[15];
  const fabric = features[16];

  return {
    vehicle,
    paint,
    fabric,
    sa: features.join(','),
  };
};

export const useImageUrl = () => {
  const request = useRequest();
  const region = request?.addressableRegion?.value;
  const vehicleOptions = useVehicleOptions();
  const features = vehicleOptions.features;

  const { data: { year = 0, vehicle = '', client } = {} } =
    useProductConfigurationQuery();

  const imageUrl = useCallback(
    (camera: ICamera, settings: IImageSettings) => {
      if (client !== 'subaru') {
        const imageProps = getImageProperties(camera, settings);
        const vehicleFeats = getVehicleFeatures(features);
        return IrisVehicleImageUrl.expand({
          region,
          ...imageProps,
          ...vehicleFeats,
        });
      }

      // the frame is default 1 for the spinner;
      let frame = (settings.cameraWaypoint || 0) + 1; // 1 // should be the waypoint for 36 frame spinner

      // If you're not on a spinner, you should have a frame number (specifically for the filename);
      if (camera.frame) {
        frame = camera.frame;
      }

      //let type = '';
      let cameraName = camera.code;
      if (camera.code.toUpperCase().startsWith('IF')) {
        cameraName = 'Interior_Details';
      }
      if (camera.code.toUpperCase().startsWith('EF')) {
        cameraName = 'Exterior_Details';
      }
      if (camera.code.toUpperCase().startsWith('RE')) {
        cameraName = 'Retailer';
      }

      const trim = features[0]; // should come from UI
      const extcolor = features[1]; //// should come from UI
      const intcolor = features[2]; // should come from UI

      const apiUrl = S3ImageUrl.expand({
        year,
        vehicle,
        trim,
        cameraName,
        frame,
        extcolor,
        intcolor,
      }); //,type});

      return apiUrl;
    },
    [region, features, vehicle, year, client]
  );

  return imageUrl;
};
